import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGiftCodes,
  deleteGiftCode,
  calculateGiftCodeStatus,
} from "@/services/firebase/giftCode";
import { GiftCode, GiftCodeStatus } from "@/types/giftCode";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ActionMenu from "@/components/common/ActionMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Gift } from "lucide-react";
import {
  AdminPage,
  AdminPageHeader,
  AdminSearchInput,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminEmptyState,
  AdminDataTable,
} from "@/components/admin-ui";
import { AdminTableColumn } from "@/components/admin-ui/admin-data-table";

// 狀態標籤配置
type StatusVariant = "success" | "danger" | "neutral";
const statusConfig: Record<GiftCodeStatus, { label: string; variant: StatusVariant }> = {
  active: { label: "啟用中", variant: "success" },
  expired: { label: "已過期", variant: "danger" },
  depleted: { label: "已用完", variant: "neutral" },
};

const columns: AdminTableColumn[] = [
  { key: "code", label: "代碼" },
  { key: "coins", label: "金幣數量", sortable: true },
  { key: "usage", label: "使用次數", sortable: true, sortKey: "used_count" },
  { key: "usageLimit", label: "每人限用", hideBelow: "md" },
  { key: "status", label: "狀態" },
  { key: "validity", label: "有效期", hideBelow: "lg" },
  { key: "createdAt", label: "建立時間", sortable: true, sortKey: "created_at", hideBelow: "lg" },
  { key: "actions", label: "操作" },
];

export default function GiftCodeListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<GiftCodeStatus | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    id: string | null;
    code: string;
  }>({ isOpen: false, id: null, code: "" });

  const { data: giftCodes, isLoading, error } = useQuery({
    queryKey: ["gift-codes"],
    queryFn: () => getGiftCodes(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteGiftCode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gift-codes"] });
      setDeleteConfirm({ isOpen: false, id: null, code: "" });
    },
    onError: (error) => {
      console.error("刪除禮物代碼失敗:", error);
      alert(`刪除禮物代碼失敗：${error instanceof Error ? error.message : "未知錯誤"}`);
    },
  });

  // 過濾和搜尋
  const filteredGiftCodes = useMemo(() => {
    if (!giftCodes || giftCodes.length === 0) return [];

    return giftCodes.filter((code) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchLower ||
        code.code.toLowerCase().includes(searchLower) ||
        (code.description && code.description.toLowerCase().includes(searchLower));

      const currentStatus = calculateGiftCodeStatus(code);
      const matchesStatus = !filterStatus || currentStatus === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [giftCodes, searchTerm, filterStatus]);

  const handleDelete = (id: string, code: string) => {
    setDeleteConfirm({ isOpen: true, id, code });
  };

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id);
    }
  };

  if (isLoading) {
    return (
      <AdminPage>
        <AdminPageHeader title="禮物代碼管理" />
        <AdminLoadingSkeleton />
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <AdminPageHeader title="禮物代碼管理" />
        <AdminErrorAlert
          message={`載入禮物代碼時發生錯誤：${error instanceof Error ? error.message : "未知錯誤"}`}
        />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title="禮物代碼管理"
        createHref="/gift-codes/new"
        createLabel="新增代碼"
      />

      {/* 操作欄 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div className="flex items-center gap-4 flex-1">
          <AdminSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="搜尋代碼或描述..."
            className="max-w-md"
          />

          {/* 狀態篩選 */}
          <div className="flex gap-1.5">
            <Button
              variant={filterStatus === null ? "default" : "outline"}
              size="sm"
              onClick={() => setFilterStatus(null)}
            >
              全部
            </Button>
            {(["active", "expired", "depleted"] as GiftCodeStatus[]).map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus(status)}
              >
                {statusConfig[status].label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* 禮物代碼列表 */}
      <AdminDataTable<GiftCode>
        columns={columns}
        data={filteredGiftCodes}
        emptyIcon={<Gift className="h-5 w-5" />}
        emptyTitle={
          searchTerm || filterStatus
            ? "沒有符合條件的禮物代碼"
            : "尚無禮物代碼資料"
        }
        emptyActionHref={!searchTerm && !filterStatus ? "/gift-codes/new" : undefined}
        emptyActionLabel="新增第一個禮物代碼"
        getRowId={(item) => item.id!}
        renderCell={(code, col) => {
          const currentStatus = calculateGiftCodeStatus(code);
          const statusInfo = statusConfig[currentStatus];
          const usagePercentage =
            code.total_uses > 0
              ? Math.round((code.used_count / code.total_uses) * 100)
              : 0;
          const remainingUses = code.total_uses - code.used_count;

          switch (col.key) {
            case "code":
              return (
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {code.code}
                  </div>
                  {code.description && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {code.description}
                    </div>
                  )}
                </div>
              );
            case "coins":
              return (
                <span className="font-semibold text-foreground">
                  {code.coins.toLocaleString()}
                </span>
              );
            case "usage":
              return (
                <div>
                  <div className="text-sm text-foreground">
                    {code.used_count} / {code.total_uses}
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 mt-1">
                    <div
                      className={`h-2 rounded-full ${
                        usagePercentage >= 100
                          ? "bg-danger"
                          : usagePercentage >= 80
                          ? "bg-warning"
                          : "bg-success"
                      }`}
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    剩餘: {remainingUses}
                  </div>
                </div>
              );
            case "usageLimit":
              return (
                <span className="text-muted-foreground">
                  {code.usage_limit_per_user} 次
                </span>
              );
            case "status":
              return (
                <Badge variant={statusInfo.variant}>
                  {statusInfo.label}
                </Badge>
              );
            case "validity":
              return (
                <div className="text-xs text-muted-foreground">
                  <div>
                    開始:{" "}
                    {code.valid_from
                      ? new Date(code.valid_from).toLocaleString("zh-TW")
                      : "-"}
                  </div>
                  <div>
                    結束:{" "}
                    {code.valid_until
                      ? new Date(code.valid_until).toLocaleString("zh-TW")
                      : "-"}
                  </div>
                </div>
              );
            case "createdAt":
              return (
                <span className="text-muted-foreground">
                  {code.created_at
                    ? new Date(code.created_at).toLocaleString("zh-TW")
                    : "-"}
                </span>
              );
            case "actions":
              return (
                <ActionMenu
                  actions={[
                    {
                      label: "詳情",
                      onClick: () => navigate(`/gift-codes/${code.id}`),
                    },
                    {
                      label: "編輯",
                      onClick: () => navigate(`/gift-codes/${code.id}/edit`),
                    },
                    {
                      label: "刪除",
                      onClick: () => handleDelete(code.id!, code.code),
                      disabled: deleteMutation.isPending,
                      variant: "danger",
                    },
                  ]}
                />
              );
            default:
              return null;
          }
        }}
      />

      {/* 刪除確認對話框 */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="確認刪除"
        message={`確定要刪除禮物代碼「${deleteConfirm.code}」嗎？此操作無法復原。`}
        confirmText="刪除"
        cancelText="取消"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, code: "" })}
      />
    </AdminPage>
  );
}
